// @ts-nocheck
import { useIsAuthenticated } from "@store/Framework/Store/Foundation/Context/ContextHooks";
import isApiError from "@insite/client-framework/Common/isApiError";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { getWishList } from "@insite/client-framework/Services/WishListService";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import setAllWishListLinesIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetAllWishListLinesIsSelected";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import { PageModel } from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import useBuyingList from "@store/Framework/Common/Hooks/useBuyingList/useBuyingList";
import useHandleApiError from "@store/Framework/Common/Hooks/useHandleApiError";
import useIsMobile from "@store/Framework/Common/Hooks/useIsMobile";
import useRedirectToSignInWithReturnAddress from "@store/Framework/Common/Hooks/useRedirectToSignInWithReturnAddress";
import { getBuyingListByWishListId } from "@store/Framework/Services/RestrictedBuyingListService";
import { useLoadLocation } from "@store/Framework/Store/Bunzl/Pages/Reporting/LoadLocations";
import PageWithMobileOverFlow from "@store/Library/Components/Reporting/Common/PageWithMobileOverFlow";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

const BuyingListDetailsPage = ({ id }: PageModel) => {
    const { buyingLists, setLoading } = useBuyingList();
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const buyingListHasLoaded = useRef(false);
    const loadLocation = useLoadLocation();
    const isLoggedIn = useIsAuthenticated();
    const redirectToSignIn = useRedirectToSignInWithReturnAddress();

    useEffect(() => {
        if (!isLoggedIn) {
            redirectToSignIn();
        }
        const setActiveBuyingList = async (id: string) => {
            try {
                setLoading(true);
                const buyingListPromises = [getBuyingListByWishListId(id), getWishList({ wishListId: id })] as const;
                const [activeBuyingList, activeWishList] = await Promise.all(buyingListPromises);
                const wishListWithBuyingListProperties = {
                    ...activeWishList,
                    properties: {
                        buyingListId: activeBuyingList.id,
                        buyingListUserCount: activeBuyingList.users.length,
                    },
                };

                setLoading(false);
                dispatch({ type: "Stores/Pages/buyingList/LoadActiveBuyingList", activeBuyingList });
                dispatch({
                    type: "Stores/Pages/buyingList/LoadActiveWishList",
                    activeWishList: wishListWithBuyingListProperties,
                });
            } catch (error) {
                buyingListHasLoaded.current = false;
                if (isApiError(error) && (error.status === 401 || error.status === 403)) {
                    redirectToSignIn();
                }
            }
        };

        if (buyingListHasLoaded.current) {
            return;
        }
        const { id: wishListId } = parseQueryString<{ id?: string }>(window.location.search);

        if (wishListId) {
            buyingListHasLoaded.current = true;
            setActiveBuyingList(wishListId);
            loadLocation();
        }
    });

    useEffect(() => {
        return () => {
            dispatch({ type: "Stores/Pages/buyingList/LoadActiveWishList", activeWishList: undefined });
            dispatch({ type: "Stores/Pages/buyingList/LoadActiveBuyingList", activeBuyingList: undefined });
            dispatch({ type: "Data/WishListLines/Reset" });
            dispatch({ type: "Data/WishLists/Reset" });
            dispatch(updateLoadWishListLinesParameter({ page: 1 }));
        };
    }, [dispatch]);

    useEffect(() => {
        if (!isLoggedIn) {
            return;
        }
        const { id: wishListId } = parseQueryString<{ id?: string }>(window.location.search);
        if (!buyingListHasLoaded.current) {
            return;
        }

        if (wishListId) {
            dispatch(setAllWishListLinesIsSelected({ isSelected: false }));
            dispatch(
                loadWishListIfNeeded({
                    wishListId,
                }),
            );
        }
    }, [buyingLists]);

    if (!buyingListHasLoaded.current || isMobile === null) {
        return null;
    }
    return (
        <PageWithMobileOverFlow>
            <Zone contentId={id} zoneName="Content"></Zone>
            <Modals />
        </PageWithMobileOverFlow>
    );
};

const pageModule = {
    component: BuyingListDetailsPage,

    definition: {
        hasEditableUrlSegment: false,
        hasEditableTitle: true,
        fieldDefinitions: [],
        pageType: "System",
    },
};

export default pageModule;
